// Hex radar for the six dimensions. Axes (clockwise from top):
// Clarity, Confidence, Conciseness, Listening, Structure, Pacing.
// Each value 0-100 maps to a vertex distance from centre.
//
// Label placement: each axis has a small <g> group positioned outside the
// ring along its axis. Inside the group, label sits on the line closest to
// the ring; the score sits on the line further from the ring. That keeps
// the score from clipping into the label or the ring fill.

import type { Dimension } from "../lib/api";

const DIMENSION_LABEL: Record<Dimension, string> = {
  clarity: "Clarity",
  confidence: "Confidence",
  conciseness: "Conciseness",
  listening: "Listening",
  structure: "Structure",
  pacing: "Pacing",
};

const ORDER: Dimension[] = ["clarity", "confidence", "conciseness", "listening", "structure", "pacing"];

const VIEW_W = 360;
const VIEW_H = 320;
const CX = VIEW_W / 2;
const CY = VIEW_H / 2;
const MAX_R = 100;
const LABEL_R = MAX_R + 22; // ring outer edge + breathing room

// Pre-computed grid hexagons.
const GRID_LEVELS = [40, 70, 100];

function vertex(r: number, idx: number): { x: number; y: number } {
  const angle = (idx * 60 - 90) * (Math.PI / 180);
  return { x: CX + r * Math.cos(angle), y: CY + r * Math.sin(angle) };
}

function gridPoints(r: number): string {
  return ORDER.map((_, i) => {
    const v = vertex(r, i);
    return `${v.x.toFixed(2)},${v.y.toFixed(2)}`;
  }).join(" ");
}

// For each axis, we want label + score vertically stacked, centred on the
// axis ray. The "above-the-vertex" axes (top three) put the score on the
// outer line (further from centre); the "below-the-vertex" axes (bottom
// three) flip so the score sits below the label.
type LabelPlacement = {
  x: number;
  y: number;
  anchor: "start" | "middle" | "end";
  /** Offset (px) of the score line relative to the label line. Positive = below. */
  scoreDy: number;
};

function placement(i: number): LabelPlacement {
  const v = vertex(LABEL_R, i);
  const anchor: LabelPlacement["anchor"] =
    i === 0 || i === 3 ? "middle" : i === 1 || i === 2 ? "start" : "end";
  // Top three axes (i=0,1,5): score sits above the label (outer = further from centre = up).
  // Bottom three axes (i=2,3,4): score sits below the label (outer = down).
  const isUpper = i === 0 || i === 1 || i === 5;
  const scoreDy = isUpper ? -14 : 16;
  return { x: v.x, y: v.y, anchor, scoreDy };
}

export function RadarChart({ scores }: { scores: Record<Dimension, number | null | undefined> }) {
  const points = ORDER.map((dim, i) => {
    const value = scores[dim] ?? 0;
    const v = vertex(MAX_R * (value / 100), i);
    return { dim, value, ...v };
  });

  const polyPoints = points.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");

  return (
    <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Voice profile radar">
      {GRID_LEVELS.map((r) => (
        <polygon key={r} className="radar-grid" points={gridPoints(r)} />
      ))}
      {ORDER.map((_, i) => {
        const v = vertex(MAX_R, i);
        return <line key={i} className="radar-axis" x1={CX} y1={CY} x2={v.x} y2={v.y} />;
      })}
      <polygon className="radar-area" points={polyPoints} />
      {points.map((p) => (
        <circle key={p.dim} className="radar-dot" cx={p.x} cy={p.y} r={3} />
      ))}
      {ORDER.map((dim, i) => {
        const p = placement(i);
        const score = scores[dim] ?? 0;
        return (
          <g key={dim}>
            <text className="radar-label" x={p.x} y={p.y} textAnchor={p.anchor}>
              {DIMENSION_LABEL[dim]}
            </text>
            <text className="radar-num" x={p.x} y={p.y + p.scoreDy} textAnchor={p.anchor}>
              {Math.round(score)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
